import { taskProgress } from '../models/task';
import { parseDateKey } from './habit-schedule';
import { deadlineKey, isPausedOn } from './task-scope';
import { todayIn } from '../utils/date-utils';
/**
 * Plain-English summaries of the things the UI draws as icons.
 *
 * A task row is a dense little pictogram: a coloured dot for priority, a flame
 * or a moon for energy, a lock, a link, a clock. Every one of those is obvious
 * once you know it and opaque until you do, and there was nowhere in the app
 * that said what any of them meant — hovering anything produced nothing.
 *
 * These build the sentence; the components decide where to hang it. Keeping it
 * here means the wording is written once, tested, and identical on a task card,
 * a schedule row and a phone list.
 */
export const PRIORITY_LABELS = {
    1: 'Very low priority',
    2: 'Low priority',
    3: 'Medium priority',
    4: 'High priority',
    5: 'Critical priority',
};
export const ENERGY_LABELS = {
    high: 'Needs high energy — deep, focused work',
    medium: 'Needs medium energy',
    low: 'Needs low energy — fine when tired',
};
export const DIFFICULTY_LABELS = {
    1: 'Very easy',
    2: 'Easy',
    3: 'Moderate',
    4: 'Hard',
    5: 'Very hard',
};
export const STATUS_LABELS = {
    todo: 'Not started',
    in_progress: 'In progress',
    completed: 'Completed',
    blocked: 'Blocked',
};
/** "1h 30m", "45m". Empty for nothing. */
export function describeDuration(minutes) {
    if (!minutes || minutes <= 0)
        return '';
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    if (hours === 0)
        return `${mins}m`;
    if (mins === 0)
        return `${hours}h`;
    return `${hours}h ${mins}m`;
}
/** "Mon 3 Aug". Returns '' for anything unparseable rather than "Invalid Date". */
export function describeDate(value) {
    if (!value)
        return '';
    // A bare YYYY-MM-DD is a local day; `new Date()` would read it as UTC
    // midnight and name the day before for anyone west of Greenwich.
    const date = /^\d{4}-\d{2}-\d{2}$/.test(value) ? (parseDateKey(value) ?? new Date(NaN)) : new Date(value);
    if (Number.isNaN(date.getTime()))
        return '';
    return date.toLocaleDateString(undefined, {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
    });
}
/**
 * The lines a tooltip shows for one task, most important first.
 *
 * Returned as an array so a caller can render them as separate lines without
 * having to split a string back apart. Nothing is included that the row
 * already states in words — the point is to explain the icons, not to repeat
 * the title.
 */
export function describeTask(task, options = {}) {
    const { project, blockedBy = [], scheduledFor, now = new Date(), timezone } = options;
    const lines = [];
    if (project?.name)
        lines.push(project.name);
    const facts = [];
    facts.push(STATUS_LABELS[task.status] ?? task.status);
    if (task.priority)
        facts.push(PRIORITY_LABELS[task.priority] ?? `Priority ${task.priority}`);
    const duration = describeDuration(task.estimated_minutes);
    if (duration)
        facts.push(task.is_habit ? duration : `${duration} estimated`);
    lines.push(facts.join(' · '));
    const shape = [];
    if (task.difficulty)
        shape.push(`${DIFFICULTY_LABELS[task.difficulty] ?? ''}`.trim());
    if (task.energy_level)
        shape.push(ENERGY_LABELS[task.energy_level] ?? '');
    const shapeLine = shape.filter(Boolean).join(' · ');
    if (shapeLine)
        lines.push(shapeLine);
    // Countable work: the number is the whole point, and 2 of 5 is invisible in
    // a status of "todo".
    if (task.target_count) {
        const unit = task.count_unit ? ` ${task.count_unit}` : '';
        lines.push(`${task.current_count ?? 0} of ${task.target_count}${unit} — ` +
            `${Math.round(taskProgress(task))}% done`);
    }
    const dueKey = deadlineKey(task, timezone);
    if (dueKey) {
        const due = describeDate(dueKey);
        if (due) {
            const days = daysUntil(dueKey, now);
            const when = days === null ? '' :
                days < 0 ? ` — ${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} overdue` :
                    days === 0 ? ' — due today' :
                        days === 1 ? ' — due tomorrow' :
                            ` — in ${days} days`;
            lines.push(`Deadline ${due}${when}`);
        }
    }
    if (scheduledFor) {
        const on = describeDate(scheduledFor);
        if (on)
            lines.push(task.is_pinned ? `Pinned to ${on}` : `Planned for ${on}`);
    }
    else if (!task.is_habit && task.status !== 'completed') {
        lines.push('Not on the plan yet');
    }
    if (blockedBy.length > 0) {
        lines.push(`Waiting on ${blockedBy.join(', ')}`);
    }
    if (isPausedOn(task, todayIn(timezone, now)))
        lines.push('Paused — not scheduled until resumed');
    if (task.needs_review)
        lines.push('Flagged for review');
    if (task.is_habit)
        lines.push('Habit — repeats, and never leaves your list');
    return lines.filter(Boolean);
}
/** Whole days from now until `date`. Negative when it has passed. */
export function daysUntil(date, now = new Date()) {
    // A bare YYYY-MM-DD is a LOCAL date. `new Date('2026-08-14')` is UTC
    // midnight — 13 August west of Greenwich — so every date-only deadline
    // read one day closer than it is for US users ("in 2 days" for 3).
    const target = /^\d{4}-\d{2}-\d{2}$/.test(date) ? (parseDateKey(date) ?? new Date(NaN)) : new Date(date);
    if (Number.isNaN(target.getTime()))
        return null;
    const a = new Date(target.getFullYear(), target.getMonth(), target.getDate());
    const b = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return Math.round((a.getTime() - b.getTime()) / 86_400_000);
}
//# sourceMappingURL=describe.js.map